import { CreateLabelPrimaryActions } from '../store/csw/primary-actions/create-label';
import { FulfillmentPrimaryActions } from '../store/csw/primary-actions/external-fulfillment';
import { LocalDeliveryPrimaryActions } from '../store/csw/primary-actions/local-delivery';
import { LocalDeliveryRatePrimaryActions } from '../store/csw/primary-actions/local-delivery-rate';
import { StandardPrimaryActions } from '../store/csw/primary-actions/standard';
import {
  getRateFromFulfillmentPlan,
  saveExternalFulfillmentConfigAndRate,
  saveConfigAndRateAsync,
} from '../store/csw/rate-action.observables';
import { getRateConfiguration, getSaveAndRateConfiguration } from '../store/csw/request-builders/shipment';
import { getExternalFulfillmentConfiguration } from '../store/csw/request-builders/external-fulfillment';
import {
  getLocalDeliverySaveAndRateConfiguration,
  getLocalDeliveryRateConfiguration,
} from '../store/csw/request-builders/local-delivery-shipment';
import { getReturnConfiguration } from '../store/csw/request-builders/return';
import {
  getSellerDefaultData,
  getFeatureFlagsThatAffectShippingConfig,
  getSalesOrderStoreTemplateData,
} from '../store/csw/request-builders/helpers';
import Global from '../store/global-selectors';
import { addPackageId } from '../fulfillment-planner/hooks/use-configure-packages';
import { FeatureFlags } from '../feature-flags/feature-flags';

export const CUSTOMERS_CREATE_LABEL = 'customers-create-label';
export const EXTERNAL_FULFILLMENTS = 'externalFulfillments';
export const LOCAL_DELIVERY = 'local-delivery';
export const LOCAL_DELIVERY_RATE = 'local-delivery-rate';
export const RATE_BROWSER = 'rate-browser';
export const RETURNS = 'returns';
export const SCAN_TO_PRINT = 'scanToPrint';
export const STANDARD = 'standard';

const getFulfillmentPlanConfiguration = (fulfillmentPlanId, state) => {
  const fp = Global.selectEntitiesFulfillmentPlanById(state, {
    fulfillmentPlanId,
  });
  // break the reference to the redux entity
  const safeFp = JSON.parse(JSON.stringify(fp));

  const accountPackages = Global.selectSellerPackages(state);
  const isPackageDropdownGenericFallbackEnabled = new FeatureFlags({ state }).enablePackageDropdownGenericFallback;
  safeFp.packages = addPackageId(safeFp.packages, accountPackages, isPackageDropdownGenericFallbackEnabled);

  return safeFp;
};

export const CSWTypes = {
  customerCreatelabel: {
    id: CUSTOMERS_CREATE_LABEL,
    primaryActions: CreateLabelPrimaryActions,
    getRateAction$: (args) => getRateFromFulfillmentPlan(args),
    getShippingConfigurationForPayload: (config, state) => {
      return getRateConfiguration(config, {
        ...getSellerDefaultData(state),
        ...getFeatureFlagsThatAffectShippingConfig(state),
      });
    },
  },
  externalFulfillments: {
    id: EXTERNAL_FULFILLMENTS,
    primaryActions: FulfillmentPrimaryActions,
    getRateAction$: (args) => saveExternalFulfillmentConfigAndRate(args),
    getShippingConfigurationForPayload: ({ fulfillmentPlanId }, state) => {
      const fp = getFulfillmentPlanConfiguration(fulfillmentPlanId, state);
      return getExternalFulfillmentConfiguration(fp);
    },
  },
  localDelivery: {
    id: LOCAL_DELIVERY,
    primaryActions: LocalDeliveryPrimaryActions,
    getRateAction$: (args) => saveConfigAndRateAsync(args),
    getShippingConfigurationForPayload: ({ fulfillmentPlanId }, state) => {
      const fp = getFulfillmentPlanConfiguration(fulfillmentPlanId, state);
      return getLocalDeliverySaveAndRateConfiguration(fp, {
        ...getSellerDefaultData(state),
        ...getFeatureFlagsThatAffectShippingConfig(state),
        ...getSalesOrderStoreTemplateData(state, fulfillmentPlanId),
      });
    },
  },
  // NOTE: Local Delivery for CUSTOMERS_CREATE_LABEL & RATE_BROWSER
  localDeliveryRate: {
    id: LOCAL_DELIVERY_RATE,
    primaryActions: LocalDeliveryRatePrimaryActions,
    getRateAction$: (args) => getRateFromFulfillmentPlan(args),
    getShippingConfigurationForPayload: (config, state) => {
      return getLocalDeliveryRateConfiguration(config, {
        ...getSellerDefaultData(state),
        ...getFeatureFlagsThatAffectShippingConfig(state),
      });
    },
  },
  rateBrowser: {
    id: RATE_BROWSER,
    primaryActions: CreateLabelPrimaryActions,
    getRateAction$: (args) => getRateFromFulfillmentPlan(args),
    getShippingConfigurationForPayload: (config, state) => {
      return getRateConfiguration(config, {
        ...getSellerDefaultData(state),
        ...getFeatureFlagsThatAffectShippingConfig(state),
      });
    },
  },
  // NOTE: returns doesn't use primaryActions() like other types
  // but needs this definition to work in our app infrastructure
  returns: {
    id: RETURNS,
    getRateAction$: (args) => getRateFromFulfillmentPlan(args),
    getShippingConfigurationForPayload: (config, state) => {
      return getReturnConfiguration(config, {
        ...getSellerDefaultData(state),
        ...getFeatureFlagsThatAffectShippingConfig(state),
      });
    },
  },
  standard: {
    id: STANDARD,
    primaryActions: StandardPrimaryActions,
    getRateAction$: (args) => saveConfigAndRateAsync(args),
    getShippingConfigurationForPayload: ({ fulfillmentPlanId }, state) => {
      const fp = getFulfillmentPlanConfiguration(fulfillmentPlanId, state);
      return getSaveAndRateConfiguration(fp, {
        ...getSellerDefaultData(state),
        ...getFeatureFlagsThatAffectShippingConfig(state),
        ...getSalesOrderStoreTemplateData(state, fulfillmentPlanId),
      });
    },
  },
};

export const CSWTypeValues = Object.values(CSWTypes);
